import React, { useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Landmark, Plus, UploadCloud, Loader2, Trash2, FileText, Euro, Building2,
  Pencil, Sparkles, ChevronRight, Users,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, apiErrorMessage } from "@/lib/api";

const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const MESES_FULL = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const REPARTO_BADGE = {
  compartido: { label: "Compartido", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  exclusivo_vdv: { label: "Excl. VDV", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  exclusivo_et: { label: "Excl. ET", cls: "bg-orange-100 text-orange-800 border-orange-200" },
};
const fmtEUR = (n) => (n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const fmtEUR2 = (n) => (n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR" });
const YEARS = [2025, 2026, 2027];

const Kpi = ({ icon: Icon, label, value, tone }) => (
  <Card><CardContent className="flex items-center gap-3 p-4">
    <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone}`}><Icon className="h-5 w-5" /></div>
    <div><p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="text-xl font-bold tabular-nums">{value}</p></div>
  </CardContent></Card>
);

const EMPTY = { month: String(new Date().getMonth() + 1), category: "servicios", reparto: "compartido", concepto: "", provider_name: "", importe: "", fecha: "" };

export default function GastosFijosPage() {
  const qc = useQueryClient();
  const [year, setYear] = useState(2026);
  const [tab, setTab] = useState("cuadro");
  const [filterMonth, setFilterMonth] = useState("all");

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [modo, setModo] = useState("factura");
  const [file, setFile] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [nominaDetail, setNominaDetail] = useState(null);
  const fileRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const metaQ = useQuery({ queryKey: ["gf-meta"], queryFn: async () => (await api.get("/gastos-fijos/meta")).data });
  const cuadroQ = useQuery({ queryKey: ["gf-cuadro", year], queryFn: async () => (await api.get("/gastos-fijos/cuadro", { params: { year } })).data });
  const movsQ = useQuery({ queryKey: ["gf-list", year], queryFn: async () => (await api.get("/gastos-fijos", { params: { year } })).data });

  const cats = metaQ.data?.categories || [];
  const repartos = metaQ.data?.repartos || [];
  const catLabel = (k) => cats.find((c) => c.key === k)?.label || k;
  const allItems = movsQ.data?.items || [];

  const catItems = useMemo(() => {
    if (!cats.length) return [];
    return allItems
      .filter((g) => g.category === tab)
      .filter((g) => filterMonth === "all" || g.month === Number(filterMonth));
  }, [allItems, tab, filterMonth]);
  const catTotal = catItems.reduce((s, g) => s + (g.importe || 0), 0);

  const openNew = (defaultCat) => {
    setEditing(null);
    setForm({ ...EMPTY, category: defaultCat || (tab !== "cuadro" ? tab : "servicios") });
    setModo("factura"); setFile(null); setOpen(true);
  };
  const openEdit = (g) => {
    setEditing(g);
    setForm({ month: String(g.month), category: g.category, reparto: g.reparto, concepto: g.concepto || "", provider_name: g.provider_name || "", importe: g.importe ?? "", fecha: g.fecha || "" });
    setModo("manual"); setFile(null); setOpen(true);
  };

  const refresh = () => { qc.invalidateQueries({ queryKey: ["gf-cuadro"] }); qc.invalidateQueries({ queryKey: ["gf-list"] }); };

  const saveMut = useMutation({
    mutationFn: async () => {
      if (editing) {
        return (await api.put(`/gastos-fijos/${editing.id}`, {
          month: Number(form.month), year, category: form.category, reparto: form.reparto,
          concepto: form.concepto, provider_name: form.provider_name, importe: form.importe === "" ? undefined : Number(form.importe), fecha: form.fecha || null,
        })).data;
      }
      const fd = new FormData();
      fd.append("year", year); fd.append("month", form.month); fd.append("category", form.category); fd.append("reparto", form.reparto);
      fd.append("concepto", form.concepto || ""); fd.append("provider_name", form.provider_name || "");
      if (form.importe !== "") fd.append("importe", form.importe);
      if (form.fecha) fd.append("fecha", form.fecha);
      if (modo === "factura" && file) fd.append("file", file);
      return (await api.post("/gastos-fijos", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (data) => {
      const est = data?.factura?.estado;
      if (!editing && est === "proforma") toast.info("Proforma detectada (no contabilizable)");
      else if (!editing && est === "error") toast.warning("Guardado, pero la IA no pudo leer el PDF");
      else toast.success(editing ? "Gasto actualizado" : "Gasto registrado");
      setOpen(false); refresh();
    },
    onError: (e) => toast.error(apiErrorMessage(e, "No se pudo guardar")),
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/gastos-fijos/${id}`)).data,
    onSuccess: () => { toast.success("Gasto eliminado"); setDelTarget(null); refresh(); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const viewPdf = async (g) => {
    try {
      const res = await api.get(`/gastos-fijos/${g.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const cuadro = cuadroQ.data;
  const annual = cuadro?.annual || {};

  const renderRows = (items) => (
    <Table data-testid="gf-mov-table">
      <TableHeader><TableRow>
        <TableHead className="w-16">Mes</TableHead>
        <TableHead>Concepto</TableHead>
        <TableHead>Proveedor</TableHead>
        <TableHead>Reparto</TableHead>
        <TableHead className="text-right">Importe</TableHead>
        <TableHead className="w-28" />
      </TableRow></TableHeader>
      <TableBody>
        {items.length === 0 ? (
          <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">Sin gastos en esta categoría. Pulsa "Añadir gasto".</TableCell></TableRow>
        ) : items.map((g) => {
          const rb = REPARTO_BADGE[g.reparto] || {};
          return (
            <TableRow key={g.id} data-testid={`gf-row-${g.id}`}>
              <TableCell className="text-xs">{MESES[g.month - 1]}</TableCell>
              <TableCell>
                <div className="flex items-center gap-1.5 font-medium leading-tight">{g.concepto || "—"}{g.source === "nominas_auto" && <Badge variant="outline" className="border-indigo-200 bg-indigo-50 text-[10px] font-normal text-indigo-700">Auto · RRHH</Badge>}</div>
                {g.cuenta_gasto?.numero && <div className="mt-0.5 text-[11px] text-muted-foreground">{g.cuenta_gasto.numero} · {g.cuenta_gasto.nombre}</div>}
              </TableCell>
              <TableCell className="text-sm">{g.provider_name || "—"}</TableCell>
              <TableCell><Badge variant="outline" className={`font-normal ${rb.cls}`}>{rb.label}</Badge></TableCell>
              <TableCell className="text-right font-semibold tabular-nums">{fmtEUR2(g.importe)}</TableCell>
              <TableCell>
                <div className="flex items-center justify-end gap-1">
                  {g.source === "nominas_auto" ? (
                    <Button variant="outline" size="sm" className="h-8" onClick={() => setNominaDetail({ year: g.year || year, month: g.month, importe: g.importe })} data-testid={`gf-nomina-detail-${g.id}`}><Users className="mr-1.5 h-3.5 w-3.5" />Ver desglose</Button>
                  ) : (
                    <>
                      {g.factura?.file_id && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => viewPdf(g)} data-testid={`gf-view-${g.id}`} aria-label="Ver PDF"><FileText className="h-4 w-4" /></Button>}
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(g)} data-testid={`gf-edit-${g.id}`} aria-label="Editar"><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDelTarget(g)} data-testid={`gf-del-${g.id}`} aria-label="Eliminar"><Trash2 className="h-4 w-4" /></Button>
                    </>
                  )}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );

  return (
    <div className="space-y-5" data-testid="gastos-fijos-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight">
            <Landmark className="h-6 w-6 text-primary" /> Gastos Fijos · Estructura
          </h1>
          <p className="text-sm text-muted-foreground">Costes de estructura VDV + ET · reparto de compartidos al 50/50</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-28" data-testid="gf-year"><SelectValue /></SelectTrigger>
            <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={() => openNew()} data-testid="gf-add"><Plus className="mr-1.5 h-4 w-4" />Añadir gasto</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi icon={Euro} label="Total año" value={fmtEUR(annual.total)} tone="bg-slate-100 text-slate-700" />
        <Kpi icon={Euro} label="A pagar VDV" value={fmtEUR(annual.pagar_vdv)} tone="bg-emerald-100 text-emerald-700" />
        <Kpi icon={Euro} label="A pagar ET" value={fmtEUR(annual.pagar_et)} tone="bg-orange-100 text-orange-700" />
        <Kpi icon={Building2} label="Compartido" value={fmtEUR(annual.compartido)} tone="bg-sky-100 text-sky-700" />
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setFilterMonth("all"); }}>
        <TabsList data-testid="gf-tabs" className="flex-wrap">
          <TabsTrigger value="cuadro" data-testid="gf-tab-cuadro">Cuadro mensual</TabsTrigger>
          {cats.map((c) => <TabsTrigger key={c.key} value={c.key} data-testid={`gf-tab-${c.key}`}>{c.label}</TabsTrigger>)}
        </TabsList>

        {/* ===== CUADRO ===== */}
        <TabsContent value="cuadro" className="mt-4">
          <Card><CardContent className="overflow-x-auto p-0">
            {cuadroQ.isLoading ? <div className="space-y-2 p-4">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div> : (
              <Table data-testid="gf-cuadro-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-card">Categoría</TableHead>
                    {MESES.map((m, i) => <TableHead key={i} className="text-right">{m}</TableHead>)}
                    <TableHead className="text-right font-bold">Anual</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(cuadro?.categories || []).map((c) => (
                    <TableRow key={c.key} className="cursor-pointer hover:bg-muted/40" onClick={() => { setFilterMonth("all"); setTab(c.key); }} data-testid={`gf-cuadro-cat-${c.key}`}>
                      <TableCell className="sticky left-0 bg-card font-medium">
                        <span className="inline-flex items-center gap-1 text-primary hover:underline">{c.label}<ChevronRight className="h-3.5 w-3.5" /></span>
                      </TableCell>
                      {MESES.map((_, i) => {
                        const cell = cuadro.matrix?.[c.key]?.[String(i + 1)] || {};
                        const v = (cell.compartido || 0) + (cell.exclusivo_vdv || 0) + (cell.exclusivo_et || 0);
                        return <TableCell key={i} className="text-right tabular-nums text-muted-foreground">{v ? fmtEUR(v) : "—"}</TableCell>;
                      })}
                      <TableCell className="text-right font-semibold tabular-nums">{fmtEUR(cuadro.cat_totals?.[c.key]?.total)}</TableCell>
                    </TableRow>
                  ))}
                  {[
                    ["solo_vdv", "Solo VDV", "text-emerald-700"],
                    ["compartido", "Compartido (VDV+ET)", "text-sky-700"],
                    ["solo_et", "Solo ET", "text-orange-700"],
                  ].map(([key, label, color]) => (
                    <TableRow key={key} className="border-t-2">
                      <TableCell className={`sticky left-0 bg-card text-xs font-semibold uppercase ${color}`}>{label}</TableCell>
                      {(cuadro?.months || []).map((mo) => <TableCell key={mo.month} className="text-right text-xs tabular-nums">{mo[key] ? fmtEUR(mo[key]) : "—"}</TableCell>)}
                      <TableCell className={`text-right text-xs font-bold tabular-nums ${color}`}>{fmtEUR(annual[key])}</TableCell>
                    </TableRow>
                  ))}
                  {[
                    ["pagar_vdv", "TOTAL A PAGAR VDV", "bg-emerald-50 text-emerald-800"],
                    ["pagar_et", "TOTAL A PAGAR ET", "bg-orange-50 text-orange-800"],
                    ["total", "TOTAL", "bg-slate-100 text-slate-900"],
                  ].map(([key, label, cls]) => (
                    <TableRow key={key} className={cls}>
                      <TableCell className={`sticky left-0 text-sm font-bold ${cls}`}>{label}</TableCell>
                      {(cuadro?.months || []).map((mo) => <TableCell key={mo.month} className="text-right text-sm font-semibold tabular-nums">{mo[key] ? fmtEUR(mo[key]) : "—"}</TableCell>)}
                      <TableCell className="text-right text-sm font-bold tabular-nums">{fmtEUR(annual[key])}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
          <p className="mt-2 text-xs text-muted-foreground">Pulsa una categoría para ver y editar su listado. La línea de <strong>Nóminas (Costes Salariales)</strong> se actualiza automáticamente al subir nóminas en RRHH.</p>
        </TabsContent>

        {/* ===== UNA PESTAÑA POR CATEGORÍA ===== */}
        {cats.map((c) => (
          <TabsContent key={c.key} value={c.key} className="mt-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <Select value={filterMonth} onValueChange={setFilterMonth}>
                  <SelectTrigger className="w-40" data-testid="gf-filter-month"><SelectValue placeholder="Mes" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los meses</SelectItem>
                    {MESES_FULL.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="text-sm text-muted-foreground">
                  <span className="font-semibold text-foreground tabular-nums">{fmtEUR2(catTotal)}</span> · {catItems.length} gasto{catItems.length === 1 ? "" : "s"}
                </div>
              </div>
              <Button size="sm" onClick={() => openNew(c.key)} data-testid={`gf-add-${c.key}`}><Plus className="mr-1.5 h-4 w-4" />Añadir gasto</Button>
            </div>
            <Card><CardContent className="p-0">
              {movsQ.isLoading ? <div className="space-y-2 p-4">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}</div> : renderRows(catItems)}
            </CardContent></Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* ===== Dialog Añadir/Editar ===== */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="gf-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar gasto fijo" : "Nuevo gasto fijo"}</DialogTitle>
            <DialogDescription>Imputa el coste a un mes, categoría y reparto VDV/ET.</DialogDescription>
          </DialogHeader>

          {!editing && (
            <div className="flex gap-2">
              <Button type="button" size="sm" variant={modo === "factura" ? "default" : "outline"} onClick={() => setModo("factura")} data-testid="gf-modo-factura"><UploadCloud className="mr-1.5 h-4 w-4" />Subir factura</Button>
              <Button type="button" size="sm" variant={modo === "manual" ? "default" : "outline"} onClick={() => setModo("manual")} data-testid="gf-modo-manual">Manual</Button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Mes</Label>
              <Select value={form.month} onValueChange={(v) => set("month", v)}>
                <SelectTrigger data-testid="gf-f-month"><SelectValue /></SelectTrigger>
                <SelectContent>{MESES_FULL.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Categoría</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="gf-f-cat"><SelectValue /></SelectTrigger>
                <SelectContent>{cats.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>Reparto</Label>
              <Select value={form.reparto} onValueChange={(v) => set("reparto", v)}>
                <SelectTrigger data-testid="gf-f-reparto"><SelectValue /></SelectTrigger>
                <SelectContent>{repartos.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 col-span-2"><Label>Concepto</Label><Input value={form.concepto} onChange={(e) => set("concepto", e.target.value)} placeholder="Ej: Coworking, Holded, Asesoría…" data-testid="gf-f-concepto" /></div>
            <div className="space-y-1.5"><Label>Proveedor</Label><Input value={form.provider_name} onChange={(e) => set("provider_name", e.target.value)} placeholder="Nombre proveedor" data-testid="gf-f-prov" /></div>
            <div className="space-y-1.5"><Label>Importe (con IVA) {modo === "factura" && !editing ? "· opcional (IA)" : ""}</Label><Input type="number" step="0.01" value={form.importe} onChange={(e) => set("importe", e.target.value)} placeholder="0.00" data-testid="gf-f-importe" /></div>
            <div className="space-y-1.5 col-span-2"><Label>Fecha factura/cargo</Label><Input type="date" value={form.fecha} onChange={(e) => set("fecha", e.target.value)} data-testid="gf-f-fecha" /></div>

            {modo === "factura" && !editing && (
              <div className="col-span-2 space-y-1.5">
                <Label>Factura (PDF)</Label>
                <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="gf-f-file-input" />
                <button type="button" onClick={() => fileRef.current?.click()} className="flex w-full items-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/30 px-4 py-3 text-sm hover:border-primary/50" data-testid="gf-f-dropzone">
                  <UploadCloud className="h-4 w-4 text-muted-foreground" />
                  {file ? <span className="font-medium">{file.name}</span> : <span className="text-muted-foreground">Selecciona el PDF de la factura</span>}
                </button>
                <p className="flex items-center gap-1 text-[11px] text-muted-foreground"><Sparkles className="h-3 w-3" />La IA rellenará importe, proveedor y fecha si los dejas vacíos.</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending || (modo === "manual" && form.importe === "" && !editing)} data-testid="gf-save">
              {saveMut.isPending ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />{modo === "factura" && !editing ? "Leyendo…" : "Guardando…"}</> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent data-testid="gf-del-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar gasto?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará “{delTarget?.concepto || delTarget?.provider_name}” ({fmtEUR2(delTarget?.importe)}).</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => delMut.mutate(delTarget.id)} data-testid="gf-confirm-del">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {nominaDetail && <NominaDesgloseDialog detail={nominaDetail} onClose={() => setNominaDetail(null)} />}
    </div>
  );
}

function NominaDesgloseDialog({ detail, onClose }) {
  const { year, month, importe } = detail;
  const periodo = `${year}-${String(month).padStart(2, "0")}`;
  const q = useQuery({
    queryKey: ["gf-nomina-desglose", periodo],
    queryFn: async () => (await api.get("/rrhh/nominas/costes", { params: { desde: periodo, hasta: periodo } })).data,
  });
  const t = q.data?.totales || {};
  const empleados = (q.data?.por_empleado || [])
    .map((e) => ({ ...e, total: (e.bruto || 0) + (e.coste_empresa || 0) }))
    .sort((a, b) => b.total - a.total);
  const totalGlobal = (t.bruto || 0) + (t.coste_empresa || 0);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="gf-nomina-desglose-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-primary" />Desglose de nóminas · {MESES_FULL[month - 1]} {year}</DialogTitle>
          <DialogDescription>Coste por empleado = Bruto + Seguridad Social a cargo de la empresa.</DialogDescription>
        </DialogHeader>

        {q.isLoading ? (
          <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
        ) : empleados.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No hay nóminas asignadas a empleados en este mes.</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border p-3"><p className="text-[11px] uppercase tracking-wide text-muted-foreground">Bruto</p><p className="text-lg font-bold tabular-nums">{fmtEUR2(t.bruto)}</p></div>
              <div className="rounded-lg border border-amber-200 p-3"><p className="text-[11px] uppercase tracking-wide text-muted-foreground">Coste empresa</p><p className="text-lg font-bold tabular-nums text-amber-600">{fmtEUR2(t.coste_empresa)}</p></div>
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3"><p className="text-[11px] uppercase tracking-wide text-muted-foreground">Coste total</p><p className="text-lg font-bold tabular-nums text-primary">{fmtEUR2(totalGlobal)}</p></div>
            </div>
            <Table data-testid="gf-nomina-desglose-table">
              <TableHeader><TableRow>
                <TableHead>Empleado</TableHead>
                <TableHead className="text-right">Bruto</TableHead>
                <TableHead className="text-right">Coste empresa</TableHead>
                <TableHead className="text-right">Coste total</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {empleados.map((e) => (
                  <TableRow key={e.user_id} data-testid={`gf-desglose-row-${e.user_id}`}>
                    <TableCell className="font-medium">{e.full_name}{e.count > 1 ? <span className="ml-1 text-xs text-muted-foreground">· {e.count} nóminas</span> : ""}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtEUR2(e.bruto)}</TableCell>
                    <TableCell className="text-right tabular-nums text-amber-600">{fmtEUR2(e.coste_empresa)}</TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">{fmtEUR2(e.total)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-t-2 bg-muted/40 font-bold">
                  <TableCell>Total ({empleados.length} {empleados.length === 1 ? "empleado" : "empleados"})</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtEUR2(t.bruto)}</TableCell>
                  <TableCell className="text-right tabular-nums text-amber-600">{fmtEUR2(t.coste_empresa)}</TableCell>
                  <TableCell className="text-right tabular-nums text-primary">{fmtEUR2(totalGlobal)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </>
        )}
        <DialogFooter>
          <Button onClick={onClose} data-testid="gf-desglose-close">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
